import React from "react";
import { IoLocationOutline, IoDocumentTextOutline } from "react-icons/io5";
import { FaRegCheckCircle, FaRegClock } from "react-icons/fa";
import { FiExternalLink } from "react-icons/fi";
import { MdOutlineRateReview } from "react-icons/md";
import {
  HiOutlineUserGroup,
  HiOutlineCurrencyDollar,
  HiOutlineLockClosed,
} from "react-icons/hi2";
import { formatDate } from "utils/helperFunctions";

export default function CompanyView({ company }) {
  if (!company) return null;

  const formatABN = (abn) => {
    if (!abn) return null;
    return abn.replace(/(\d{2})(\d{3})(\d{3})(\d{3})/, "$1 $2 $3 $4");
  };

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toLocaleString("en-AU", { minimumFractionDigits: 0 })}`;
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      active: "bg-green-100 text-green-800",
      pending: "bg-yellow-100 text-yellow-800",
      suspended: "bg-red-100 text-red-800",
      inactive: "bg-lightgray text-darkgray",
    };
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${
          statusStyles[status] || statusStyles.active
        }`}
      >
        {status?.charAt(0).toUpperCase() + status?.slice(1) || "Active"}
      </span>
    );
  };

  const getBillingTypeLabel = (type) => {
    const labels = {
      perSeat: "per seat/month",
      perProperty: "per property",
      flat: "per month",
    };
    return labels[type] || type;
  };

  // Check which required documents are signed
  const getDocumentStatus = () => {
    if (!company.requiredDocuments || company.requiredDocuments.length === 0) {
      return { signed: [], pending: [] };
    }

    const signed = [];
    const pending = [];

    company.requiredDocuments.forEach((doc) => {
      const docId = typeof doc === "string" ? doc : doc._id;
      const agreedTerm = (company.agreedTerms || []).find((term) => {
        const termDocId =
          typeof term.documentId === "string"
            ? term.documentId
            : term.documentId?._id;
        return termDocId === docId;
      });

      if (agreedTerm) {
        signed.push({ document: doc, agreedTerm });
      } else {
        pending.push(doc);
      }
    });

    return { signed, pending };
  };

  const documentStatus = getDocumentStatus();

  return (
    <div className="bg-white">
      {/* Header */}
      <div className="bg-lightgray p-6 rounded-lg mb-6">
        <div className="flex justify-between items-start">
          <div>
            <div className="text-base font-semibold text-black mb-2 flex items-center gap-2">
              {company.name}
              {company.isEnterprise && (
                <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                  Enterprise
                </span>
              )}
            </div>
            <p className="text-darkgray">
              {company.offices?.length || 0} office
              {company.offices?.length !== 1 ? "s" : ""} registered
            </p>
          </div>
          {getStatusBadge(company.status)}
        </div>
      </div>

      {/* Content */}
      <div className="space-y-6">
        {/* Organization */}
        <div>
          <div className="font-medium text-black mb-3">Organization</div>
          {company.organizationId ? (
            <div className="bg-lightgray rounded-lg p-4 flex justify-between">
              <span className="text-darkgray">Organization:</span>
              <span className="text-black font-medium">
                {company.organizationId.name}
              </span>
            </div>
          ) : (
            <p className="text-darkgray italic">No organization assigned</p>
          )}
        </div>

        {/* Owner Information */}
        <div>
          <div className="font-medium text-black mb-3">Owner Information</div>
          {company.primaryOwner ? (
            <div className="bg-lightgray rounded-lg p-4 space-y-3">
              <div className="flex justify-between">
                <span className="text-darkgray">Primary Owner:</span>
                <span className="text-black font-medium">
                  {company.primaryOwner.name}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-darkgray">Email:</span>
                <span className="text-black">{company.primaryOwner.email}</span>
              </div>
              {company.primaryOwner.phone && (
                <div className="flex justify-between">
                  <span className="text-darkgray">Phone:</span>
                  <span className="text-black">{company.primaryOwner.phone}</span>
                </div>
              )}
              {company.licenseeInCharge && (
                <div className="flex justify-between">
                  <span className="text-darkgray">Licensee in Charge:</span>
                  <span className="text-black font-medium">
                    {company.licenseeInCharge.name}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <p className="text-darkgray italic">No owner assigned</p>
          )}
        </div>

        {/* Secondary Owners, then Company Admins */}
        {[
          { title: "Secondary Owners", people: company.coOwners },
          { title: "Company Admins", people: company.administrators },
        ]
          .filter(({ people }) => people?.length > 0)
          .map(({ title, people }) => (
            <div key={title}>
              <div className="font-medium text-black mb-3 flex items-center gap-2">
                <HiOutlineUserGroup />
                {title} ({people.length})
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                {people.map((person, index) => (
                  <div
                    key={person._id || index}
                    className="bg-lightgray rounded-lg p-4 border-l-4 border-purple-500"
                  >
                    <div className="font-medium text-black">{person.name}</div>
                    <div className="text-sm text-darkgray">{person.email}</div>
                    {person.phone && (
                      <div className="text-sm text-darkgray">{person.phone}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

        {/* Default Co-Agents */}
        {company.defaultCoAgents && company.defaultCoAgents.length > 0 && (
          <div>
            <div className="font-medium text-black mb-3 flex items-center gap-2">
              <HiOutlineUserGroup />
              Default Co-Agents ({company.defaultCoAgents.length})
            </div>
            <p className="text-sm text-darkgray mb-3">
              Auto-added as followers on every new auth schedule for this
              company
            </p>
            <div className="grid gap-3 md:grid-cols-2">
              {company.defaultCoAgents.map((agent, index) => (
                <div
                  key={agent._id || index}
                  className="bg-lightgray rounded-lg p-4 border-l-4 border-blue-500"
                >
                  <div className="font-medium text-black">{agent.name}</div>
                  <div className="text-sm text-darkgray">{agent.email}</div>
                  {agent.phone && (
                    <div className="text-sm text-darkgray">{agent.phone}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Business Information */}
        <div>
          <div className="font-medium text-black mb-3">
            Business Information
          </div>
          <div className="bg-lightgray rounded-lg p-4 space-y-3">
            {company.businessName && (
              <div className="flex justify-between">
                <span className="text-darkgray">Business Name:</span>
                <span className="text-black">{company.businessName}</span>
              </div>
            )}
            {company.abn && (
              <div className="flex justify-between">
                <span className="text-darkgray">ABN:</span>
                <span className="text-black">{formatABN(company.abn)}</span>
              </div>
            )}
            {company.licenseNumber && (
              <div className="flex justify-between">
                <span className="text-darkgray">License Number:</span>
                <span className="text-black">{company.licenseNumber}</span>
              </div>
            )}
            {company.corporateLicense?.number && (
              <>
                <div className="flex justify-between">
                  <span className="text-darkgray">Corporate License:</span>
                  <span className="text-black">
                    {company.corporateLicense.number}
                    {company.corporateLicense.isValid ? "" : " (not valid)"}
                    {company.corporateLicense.isExpired ? " (expired)" : ""}
                  </span>
                </div>
                {company.corporateLicense.issuedTo && (
                  <div className="flex justify-between">
                    <span className="text-darkgray">Issued To:</span>
                    <span className="text-black">
                      {company.corporateLicense.issuedTo}
                    </span>
                  </div>
                )}
                {company.corporateLicense.expiry && (
                  <div className="flex justify-between">
                    <span className="text-darkgray">License Expiry:</span>
                    <span className="text-black">
                      {new Date(
                        company.corporateLicense.expiry,
                      ).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </>
            )}
            <div className="flex justify-between">
              <span className="text-darkgray">Company Type:</span>
              <span className="text-black">
                {company.isEnterprise ? "Enterprise" : "Standard"}
              </span>
            </div>
            {company.reviewLink && (
              <div className="flex justify-between items-center">
                <span className="text-darkgray">Review Link:</span>
                <a
                  href={company.reviewLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                >
                  <MdOutlineRateReview className="text-lg" />
                  View Reviews
                  <FiExternalLink className="text-sm" />
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Enterprise Pricing - Only show for enterprise companies */}
        {company.isEnterprise && company.enterprisePricing && (
          <div>
            <div className="font-medium text-black mb-3">
              Enterprise Pricing
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
              {/* Main Agent Pricing */}
              <div className="flex justify-between items-center">
                <div>
                  <span className="font-medium">
                    {company.enterprisePricing.mainAgent?.name || "Main Agent"}
                  </span>
                  <p className="text-sm text-darkgray">Base seat pricing</p>
                </div>
                <span className="text-lg font-semibold">
                  {formatCurrency(
                    company.enterprisePricing.mainAgent?.unitPrice || 699,
                  )}
                  /seat/month
                </span>
              </div>

              {/* Addons */}
              {company.enterprisePricing.addons &&
                company.enterprisePricing.addons.length > 0 && (
                  <div className="pt-3 border-t border-blue-200">
                    <p className="text-sm font-medium mb-3">Addons</p>
                    <div className="space-y-2">
                      {company.enterprisePricing.addons.map((addon, index) => (
                        <div
                          key={addon.key || index}
                          className={`flex justify-between items-center p-2 rounded ${
                            addon.removable === false
                              ? "bg-purple-50 border border-purple-200"
                              : "bg-white"
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-black">{addon.name}</span>
                            {addon.removable === false && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-200 text-purple-700 text-xs rounded-full">
                                <HiOutlineLockClosed className="w-3 h-3" />
                                Required
                              </span>
                            )}
                          </div>
                          <span className="text-sm font-medium">
                            {formatCurrency(addon.unitPrice)}{" "}
                            {getBillingTypeLabel(addon.billingType)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          </div>
        )}

        {/* Offices */}
        <div>
          <div className="font-medium text-black mb-3">
            Offices ({company.offices?.length || 0})
          </div>
          {company.offices && company.offices.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2">
              {company.offices.map((office, index) => (
                <div
                  key={office._id || index}
                  className="bg-lightgray rounded-lg p-4 border-l-4 border-blue-500"
                >
                  <div className="font-medium text-black mb-2">
                    {office.name || `Office ${index + 1}`}
                  </div>
                  {office.address && (
                    <div className="flex items-center gap-2 mb-2">
                      <IoLocationOutline />
                      <span className="text-darkgray text-sm">
                        {office.address}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-darkgray italic">No offices registered</p>
          )}
        </div>

        {/* Required Documents Status */}
        <div>
          <div className="font-medium text-black mb-3">
            Required Documents ({company.requiredDocuments?.length || 0})
          </div>

          {company.requiredDocuments && company.requiredDocuments.length > 0 ? (
            <div className="space-y-3">
              {/* Signed Documents */}
              {documentStatus.signed.map(({ document, agreedTerm }, index) => (
                <div
                  key={index}
                  className="bg-lightgray rounded-lg p-4 border-l-4 border-green-500"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-start gap-2 mb-2">
                        <FaRegCheckCircle className="text-green-600 text-lg mt-0.5" />
                        <div>
                          <p className="font-medium">
                            {document.name?.replace(/_/g, " ") || "Document"}
                          </p>
                          <p className="text-sm text-darkgray">
                            Signed: {formatDate(agreedTerm.acceptedAt)}
                          </p>
                        </div>
                      </div>
                    </div>
                    {agreedTerm.documentUrl && (
                      <a
                        href={agreedTerm.documentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center ml-4"
                      >
                        <FiExternalLink className="mr-1" />
                        View
                      </a>
                    )}
                  </div>
                </div>
              ))}

              {/* Pending Documents */}
              {documentStatus.pending.map((document, index) => (
                <div
                  key={index}
                  className="bg-lightgray rounded-lg p-4 border-l-4 border-yellow-500"
                >
                  <div className="flex items-start gap-2">
                    <FaRegClock className="text-yellow-600 text-lg mt-0.5" />
                    <div>
                      <p className="font-medium">
                        {document.name?.replace(/_/g, " ") || "Document"}
                      </p>
                      <p className="text-sm text-yellow-700">
                        Pending signature
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-lightgray rounded-lg p-6 flex flex-col gap-2 items-center justify-center">
              <IoDocumentTextOutline className="text-4xl text-center text-darkgray" />
              <p className="text-darkgray italic mt-4">
                No documents required for this company
              </p>
            </div>
          )}
        </div>

        {/* All Signed Documents */}
        {company.agreedTerms && company.agreedTerms.length > 0 && (
          <div>
            <div className="font-medium text-black mb-3">
              All Signed Documents ({company.agreedTerms.length})
            </div>
            <div className="space-y-3">
              {company.agreedTerms.map((term, index) => (
                <div
                  key={index}
                  className="bg-lightgray rounded-lg p-4 border-l-4 border-green-500"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-start gap-2 mb-2">
                        <FaRegCheckCircle className="text-green-600 text-lg mt-0.5" />
                        <div>
                          <p className="font-medium">
                            {term.documentId?.name?.replace(/_/g, " ") ||
                              "Document"}
                          </p>
                          <p className="text-sm text-darkgray">
                            Accepted: {formatDate(term.acceptedAt)}
                          </p>
                        </div>
                      </div>
                    </div>
                    {term.documentUrl && (
                      <a
                        href={term.documentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center ml-4"
                      >
                        <FiExternalLink className="mr-1" />
                        View
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
